import { Button, FormField, Input, Modal } from '@cloudscape-design/components';
import { ICiteBlock } from './chatWithSummaryNew';
import { getKBDocLinkAndOpen } from '../../../helpers/getAndOpenLink';
import { Pin, PinOff, SearchIcon, X } from 'lucide-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { DocumentContext } from '../../../context/documentContext';
import { escapeAndStripEndingPunctuation } from './chatHelpers';
import { IRFP_Context } from '../../../lib/models/interfaces';

interface CiteBlockCardProps {
  citeBlock: ICiteBlock | IRFP_Context;
  pinCiteBlock: (id: string, pinAdd: boolean) => void;
  auth: any;
  pinAdd: boolean;
  view: string;
  goToSpecificPage: (pageNo: number, index: number) => void;
  closeModal: (arg: boolean) => void;
  docIndex?: number;
  showSearchBox?: boolean;
  snippetInfo?: { [key: string]: string[] };
  citeIndex?: number | null;
}

const getHighlightedText = (text: string, highlight: string) => {
  if (!highlight) return text;
  const escapedHighlight = escapeAndStripEndingPunctuation(highlight);
  console.log('escaped highlight text in getHighlights', escapedHighlight);
  // Create regex with case insensitive global match
  const regex = new RegExp(`(${escapedHighlight})`, 'gi');

  // Split text by matched parts, keeping the matches
  const parts = text.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? <mark key={index}>{part}</mark> : part
  );
};

const CiteBlockCard = ({
  citeBlock,
  auth,
  pinCiteBlock,
  pinAdd,
  view,
  goToSpecificPage,
  closeModal,
  docIndex,
  showSearchBox,
  snippetInfo = {},
  citeIndex = null
}: CiteBlockCardProps) => {
  const { allDocuments } = useContext(DocumentContext);
  const [highLightText, setHighLightText] = useState<string>('');
  const [showBlockedModal, setShowBlockedModal] = useState<boolean>(false);
  const isThisDocAllowed = allDocuments.filter(
    (ele) => ele._id === citeBlock.Document._id
  );

  useEffect(() => {
    if (snippetInfo && citeBlock) {
      if (snippetInfo[citeBlock._id]) {
        const firstPhrase = snippetInfo[citeBlock._id];
        if (firstPhrase?.length > 0) {
          console.log('setting phrase', firstPhrase[0]);
          const escapesRemoved = firstPhrase[0].replace(/\\/g, '');
          setHighLightText(escapesRemoved);
        }
      }
    } else {
      setHighLightText('');
    }
  }, [snippetInfo, citeBlock]);
  type BrowserType = 'chrome' | 'edge' | 'firefox' | 'safari' | 'other';
  const browser: BrowserType = useMemo(() => {
    if (typeof navigator === 'undefined') return 'other';
    const ua = navigator.userAgent.toLowerCase();

    if (ua.includes('edg/')) return 'edge';
    if (ua.includes('chrome/')) return 'chrome';
    if (ua.includes('firefox/')) return 'firefox';
    if (ua.includes('safari/')) return 'safari';
    return 'other';
  }, []);

  return (
    <div className="border border-gray-300 p-4 my-4 rounded-xl shadow-sm">
      <div className="flex items-center justify-between mb-4 ">
        <div className="text-xl font-semibold text-gray-700">
          {citeIndex ? `Citation Block - ${citeIndex}` : 'Citation Block'}
        </div>
        {citeBlock && showSearchBox && (
          <div className="flex flex-row items-center">
            <Input
              value={highLightText}
              onChange={({ detail }) => setHighLightText(detail.value)}
              placeholder="Search Term"
            ></Input>
            <X size={16} onClick={() => setHighLightText('')} />
          </div>
        )}
        {!(view === 'rfp') ? (
          <div className="text-blue-500 p-2 rounded-full bg-blue-100">
            {pinAdd ? (
              <Pin
                onClick={() => pinCiteBlock(citeBlock._id, pinAdd)}
                size={20}
              />
            ) : (
              <PinOff
                onClick={() => pinCiteBlock(citeBlock._id, pinAdd)}
                size={20}
              />
            )}
          </div>
        ) : null}
      </div>
      {isThisDocAllowed.length > 0 ? (
        <>
          <p>
            <strong>Text:</strong>{' '}
            {highLightText.length > 0
              ? getHighlightedText(citeBlock.text, highLightText)
              : citeBlock.text}
          </p>
          <p>
            <strong>Document:</strong> {citeBlock.Document.documentFileName}
          </p>
          <p>
            <strong>Page:</strong>{' '}
            {citeBlock?.pageNumber ?? citeBlock?.PageNumber}
          </p>
          {citeBlock?.score && (
            <p>
              <strong>Score:</strong>{' '}
              {`${Number(citeBlock?.score.toFixed(2)) * 100} %`}
            </p>
          )}
          {/* <p>{citeBlock._id}</p> */}
        </>
      ) : (
        <>
          <div>
            You do not have view permissions on a document that is part of the
            citation.
          </div>
        </>
      )}
      {view !== 'simple' && (
        <Button
          onClick={() =>
            getKBDocLinkAndOpen(
              citeBlock.Document._id,
              citeBlock.pageNumber ?? citeBlock.PageNumber,
              auth,
              setShowBlockedModal
            )
          }
          href=""
          variant="link"
          disabled={!(isThisDocAllowed.length > 0)}
        >
          {citeBlock.Document.documentFileName}
        </Button>
      )}
      {view === 'simple' && (
        <Button
          onClick={() => {
            goToSpecificPage(citeBlock.pageNumber, docIndex ?? 0);
            closeModal(false);
          }}
        >
          Show Page
        </Button>
      )}
      <Modal
        visible={showBlockedModal}
        onDismiss={() => setShowBlockedModal(false)}
      >
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div className="font-semibold mb-1">
            Pop-up blocked by your browser
          </div>
          <p className="mb-2">
            Please allow pop-ups for this site, then click the button again.
          </p>

          {browser === 'chrome' || browser === 'edge' ? (
            <ol className="list-decimal list-inside space-y-1">
              <li>
                Look for the pop-up icon in the address bar (a window with an
                “X”).
              </li>
              <li>
                Click it and choose “Always allow pop-ups from this site”.
              </li>
              <li>Refresh the page and click the button again.</li>
            </ol>
          ) : browser === 'firefox' ? (
            <ol className="list-decimal list-inside space-y-1">
              <li>
                Look for a bar or icon saying a pop-up was blocked near the top
                of the window.
              </li>
              <li>Click “Options” or the icon.</li>
              <li>Choose “Allow pop-ups for this site”.</li>
            </ol>
          ) : browser === 'safari' ? (
            <ol className="list-decimal list-inside space-y-1">
              <li>
                On macOS: Go to{' '}
                <strong>
                  Safari &gt; Settings &gt; Websites &gt; Pop-up Windows
                </strong>
                .
              </li>
              <li>
                Find this site in the list and change it to{' '}
                <strong>Allow</strong>.
              </li>
              <li>Reload the page and click the button again.</li>
            </ol>
          ) : (
            <ol className="list-decimal list-inside space-y-1">
              <li>Your browser has blocked the pop-up window for this site.</li>
              <li>
                Look for a small icon or banner near the address bar mentioning
                “pop-up blocked”.
              </li>
              <li>
                Click it and choose to allow pop-ups for this site, then try
                again.
              </li>
            </ol>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CiteBlockCard;
